import { useState, useCallback, useMemo, Fragment } from 'react'
import tw from 'tailwind-styled-components'
import { useTranslation } from 'next-i18next'
import { Collapse } from 'react-collapse'
import { useEmployeesQuery } from '@foros-fe/graphql'
import { useDeviceMediaQuery } from '../hooks'
import { clientOnlyComponent, arrayToChunks } from '../utils'
import { AboutUsCard } from './AboutUsCard'
import { CloseButton } from './CloseButton'
import { Button } from './Button'
import { SvgIcon } from './SvgIcon'
import LinkedIn from '@foros-fe/assets/icons/linkedin.svg'

const Container = tw.div`flex flex-1 flex-col w-screen`
const PersonContainer = tw.div`container grid grid-flow-row grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 justify-center`
const CollapseContainer = tw.div`w-full bg-gray-200`
const InfoContainer = tw.div`flex flex-col lg:flex-row bg-gray-200 container py-11`
const Name = tw.p`text-lg font-medium mb-1`
const Description = tw.div`text-gray-800 mr-2 lg:mr-auto`

export const AboutUsList = clientOnlyComponent(() => {
  const { t } = useTranslation(['about', 'enum'])
  const { data } = useEmployeesQuery({ fetchPolicy: 'cache-first' })
  const employees = useMemo(
    () => data?.countryRepresentatives.flatMap(({ representatives }) => representatives) || [],
    [data]
  )
  const [open, setOpen] = useState<boolean>(false)
  const [activeItem, setActiveItem] = useState<null | string>(null)
  const [showMore, setShowMore] = useState(false)
  const { desktop, tablet } = useDeviceMediaQuery()
  const openInfoBox = useCallback(
    (id: string) => {
      if (open) {
        if (id === activeItem) {
          setOpen(false)
        }
      } else {
        setOpen(true)
      }

      if (id !== activeItem) {
        setActiveItem(id)
      }
    },
    [open, activeItem]
  )
  const closeInfoBox = useCallback(() => {
    setOpen(false)

    setTimeout(() => {
      setActiveItem(null)
    }, 700)
  }, [])
  const items = useMemo(
    () => arrayToChunks(showMore ? employees : employees.slice(0, 8), desktop ? 4 : tablet ? 2 : 1),
    [desktop, tablet, showMore, employees]
  )
  const currentPerson = useMemo(
    () => employees.find(({ id }) => id === activeItem),
    [activeItem, employees]
  )

  return (
    <Container>
      {items.map((chunk, index) => (
        <Fragment key={index}>
          <PersonContainer>
            {chunk.map((person) => (
              <AboutUsCard key={person.id} onClick={openInfoBox} {...person} />
            ))}
          </PersonContainer>
          <Collapse
            theme={{
              collapse: 'duration-700 my-6',
            }}
            isOpened={open && !!chunk.find(({ id }) => id === activeItem)}
          >
            <CollapseContainer>
              <InfoContainer>
                {desktop ? (
                  <>
                    <div className='mr-24'>
                      {currentPerson && (
                        <Name>
                          {currentPerson.firstName} {currentPerson.lastName}
                        </Name>
                      )}
                      <p>
                        {currentPerson
                          ? t(`enum:RepresentativeTitle.${currentPerson.title}`)
                          : null}
                      </p>
                      {currentPerson?.linkedinUrl ? (
                        <a
                          className='mt-4 inline-block'
                          href={currentPerson.linkedinUrl}
                          target='_blank'
                          rel='noreferrer'
                        >
                          <SvgIcon src={LinkedIn} size={32} />
                        </a>
                      ) : null}
                    </div>
                    <Description
                      className='max-w-[625px] mr-auto'
                      dangerouslySetInnerHTML={{ __html: currentPerson?.description || '' }}
                    />
                    <CloseButton onClick={closeInfoBox} />
                  </>
                ) : (
                  <>
                    <div className='flex flex-1 justify-between mb-8'>
                      <div>
                        {currentPerson && (
                          <Name>
                            {currentPerson.firstName} {currentPerson.lastName}
                          </Name>
                        )}
                        <p>{currentPerson?.title}</p>
                        {currentPerson?.linkedinUrl ? (
                          <a
                            className='mt-4 inline-block'
                            href={currentPerson.linkedinUrl}
                            target='_blank'
                            rel='noreferrer'
                          >
                            <SvgIcon src={LinkedIn} size={32} />
                          </a>
                        ) : null}
                      </div>
                      <CloseButton className='mr-1' onClick={closeInfoBox} />
                    </div>
                    <Description
                      dangerouslySetInnerHTML={{ __html: currentPerson?.description || '' }}
                    />
                  </>
                )}
              </InfoContainer>
            </CollapseContainer>
          </Collapse>
        </Fragment>
      ))}
      {showMore ? null : (
        <Button
          value='about-us-load-more'
          title={t('loadMoreTeam')}
          outline
          className='self-center'
          onClick={() => setShowMore(true)}
        />
      )}
    </Container>
  )
})
